/**
 * Place-on-Hold: brand-optional enhancements to the existing `billingHoldUntil` billing hold
 * (#33568 / S1 — #33624).
 *
 * There is ONE hold — `policy.billingHoldUntil` — and it is always staff-placed. This module does not
 * introduce a new hold concept; it adds behavior and brand-config-gated options on top of the existing
 * field:
 *   - an active hold suppresses all automated billing + invoicing (extends today's next-charge skip;
 *     a no-op for brands like Branch that have no direct-bill / invoicing beyond install pay);
 *   - mutual brands may additionally require a reason and offer a "hold cancellations as well" option
 *     (`billingHoldBlocksCancellations`) that stops automated cancellation-for-nonpayment.
 *
 * The hold auto-releases by expiry — once `billingHoldUntil` passes, everything resumes; no release job
 * is needed. Lives in `@ourbranch/policy-utils` (audience: universal) so staff-fe, the resolver, and the
 * AR/cancel crons share the same predicates.
 */
import { addDays, parseISO, format } from 'date-fns';
import { getTodayStr } from '@ourbranch/date-fns-helpers';

/** The billing-hold fields carried on a policy. All additive / nullable. */
export interface BillingHoldFields {
  /** AWSDate (yyyy-MM-dd). The existing field — the hold is in effect through this date. */
  billingHoldUntil?: string | null;
  billingHoldReason?: string | null;
  /** Mutual-only: when true, automated cancellation-for-nonpay is suppressed while the hold is active. */
  billingHoldBlocksCancellations?: boolean | null;
  billingHoldPlacedBy?: string | null;
  /** ISO timestamp. */
  billingHoldPlacedAt?: string | null;
}

/**
 * True while a billing hold is in effect. Compared as AWSDate strings (`yyyy-MM-dd` order
 * chronologically as text). The caller must pass a STATE-AWARE business date for `todayStr`
 * (e.g. `getTodayStr(policy.state)` from `@ourbranch/date-helpers`), so a Lambda's default UTC clock
 * does not release a hold a day early/late. Drives suppression of all automated billing + invoicing.
 */
export function isBillingHoldActive(policy: BillingHoldFields | null | undefined, todayStr: string): boolean {
  return !!policy?.billingHoldUntil && todayStr <= policy.billingHoldUntil;
}

/**
 * Convenience for backend AR/billing jobs that have the policy loaded and just need a yes/no: is a
 * billing hold active as-of today in the policy's state timezone. Resolves the state-aware "today"
 * (`getTodayStr(policy.state)`) so callers don't each re-derive it. Drives suppression of automated
 * billing + invoicing while a hold is in effect.
 */
export function isPolicyBillingHoldActive(
  policy: (BillingHoldFields & { state?: string | null }) | null | undefined
): boolean {
  return isBillingHoldActive(policy, getTodayStr(policy?.state));
}

/**
 * True while an active hold also blocks automated cancellation-for-nonpayment. Only mutual brands can
 * set `billingHoldBlocksCancellations` (the staff checkbox is brand-gated), so this is always false for
 * brands like Branch.
 */
export function holdBlocksCancellations(policy: BillingHoldFields | null | undefined, todayStr: string): boolean {
  return isBillingHoldActive(policy, todayStr) && policy?.billingHoldBlocksCancellations === true;
}

/** The latest date a hold may be set to, given a state-aware `todayStr` and the brand's max-days cap. */
export function getBillingHoldMaxDate(todayStr: string, maxHoldDays: number): string {
  return format(addDays(parseISO(todayStr), maxHoldDays), 'yyyy-MM-dd');
}

export interface PlaceOnHoldInput {
  billingHoldUntil?: string | null;
  billingHoldReason?: string | null;
  billingHoldBlocksCancellations?: boolean | null;
}

export interface PlaceOnHoldBrandConfig {
  /** Business "today" in the policy's state timezone (e.g. `getTodayStr(policy.state)`). */
  todayStr: string;
  /** Max days a hold may run (brand config; Branch 8, mutuals 15). */
  maxHoldDays: number;
  /** Whether a reason is required (brand config; mutuals on, Branch off). */
  requireReason?: boolean;
  /** Whether the "hold cancellations as well" option is available (brand config; mutuals on). */
  holdCancellationsEnabled?: boolean;
}

/**
 * Validates a Place-on-Hold request against brand config. Returns user-facing error messages; an empty
 * list means valid. Pure (no I/O) so staff-fe and the resolver share it.
 */
export function validatePlaceOnHold(
  input: PlaceOnHoldInput,
  { todayStr, maxHoldDays, requireReason, holdCancellationsEnabled }: PlaceOnHoldBrandConfig
): string[] {
  const errors: string[] = [];

  if (!input.billingHoldUntil) {
    errors.push('A hold date is required.');
  } else {
    if (input.billingHoldUntil < todayStr) {
      errors.push('Hold date cannot be in the past.');
    }
    if (input.billingHoldUntil > getBillingHoldMaxDate(todayStr, maxHoldDays)) {
      errors.push(`Hold date cannot be more than ${maxHoldDays} days out.`);
    }
  }

  if (requireReason && !input.billingHoldReason?.trim()) {
    errors.push('Hold Reason is required.');
  }

  if (input.billingHoldBlocksCancellations && !holdCancellationsEnabled) {
    errors.push('Holding cancellations is not enabled for this brand.');
  }

  return errors;
}
